/**
 * Full-text search over the Help Centre, in the browser. The content is small,
 * so the index is a list of entries matched in memory. Matching ignores case
 * and accents, and every word of the query must start a word in the entry's
 * title or text ("rep" finds "report"; "port" doesn't).
 */
import {
	HelpDefaultFaq,
	ReaderSection,
	ReaderTopic,
} from "../../types/helpCentre.types";
import { helpFaqPath, helpTopicPath } from "./content";

export type HelpSearchKind =
	"topic" | "section" | "tab" | "faq" | "video" | "defaultFaq";

export interface HelpSearchEntry {
	kind: HelpSearchKind;
	title: string;
	/** Plain text: markdown is reduced to its words. */
	body: string;
	/** Where the entry sits, such as "Getting started › Common questions". */
	context: string;
	/** The page that shows it, with any tab or FAQ to open. */
	to: string;
	topicKey?: string;
	sectionKey?: string;
	/** Position in the index, which follows the server's order. */
	order: number;
	foldedTitle: string;
	foldedBody: string;
}

/** A half-open `[start, end)` span of a string, to highlight. */
export type TextRange = [number, number];

export interface HelpSearchHighlights {
	titleRanges: TextRange[];
	/** About `SNIPPET_LENGTH` characters of the body around its first match. */
	snippet: string;
	snippetRanges: TextRange[];
}

const MIN_TERM_LENGTH = 2;
const SNIPPET_LENGTH = 160;
/** How much of the snippet comes before the first match. */
const SNIPPET_LEAD = 40;
const ELLIPSIS = "…";

const KIND_WEIGHT: Record<HelpSearchKind, number> = {
	topic: 3,
	section: 2,
	tab: 2,
	faq: 1,
	video: 1,
	defaultFaq: 1,
};

/** Lower-case letters such as æ and ø that Unicode doesn't decompose, as the server's `slugify` spells them. */
const LETTERS: Record<string, string> = {
	æ: "ae",
	ø: "o",
	œ: "oe",
	ß: "ss",
	đ: "d",
	ð: "d",
	þ: "th",
	ł: "l",
};

const WORD_CHAR = /[\p{L}\p{N}]/u;
const NOT_WORD = /[^\p{L}\p{N}]+/u;

/** True for the accents NFKD splits off (U+0300 to U+036F). */
const isCombiningMark = (char: string) => {
	const code = char.codePointAt(0) ?? 0;
	return code >= 0x300 && code <= 0x36f;
};

export interface FoldedText {
	folded: string;
	/** `map[i]` is the index in the original text of folded character `i`. */
	map: number[];
}

/** Lower case, with accents removed and æ ø œ ß đ ð þ ł spelled out. */
export const foldText = (text: string): FoldedText => {
	let folded = "";
	const map: number[] = [];
	for (let index = 0; index < text.length; index++) {
		const lower = text[index].toLowerCase();
		const spelled = LETTERS[lower] ?? lower.normalize("NFKD");
		for (const char of spelled) {
			if (isCombiningMark(char)) continue;
			folded += char;
			map.push(index);
		}
	}
	return { folded, map };
};

/** The words of a query that count: folded, at least 2 characters, each once. */
export const queryTerms = (query: string): string[] => [
	...new Set(
		foldText(query)
			.folded.split(NOT_WORD)
			.filter((term) => term.length >= MIN_TERM_LENGTH)
	),
];

/** A code fence line, such as ```js. */
const FENCE_LINE = /^\s*(?:`{3,}|~{3,})/;
/** A table's separator row or a horizontal rule: only pipes, colons, hyphens and spaces. */
const RULE_LINE = /^[\s|:-]+$/;
/** A heading, quote or list marker at the start of a line. */
const LINE_MARKER = /^\s*(?:#{1,6}|>+|[-*+]|\d{1,9}[.)])\s+/;

/**
 * The markdown's words, without its syntax: for searching and snippets. Line
 * syntax is dropped line by line, and the inline patterns are bounded, so an
 * unclosed bracket can't make a long article slow to index.
 */
export const markdownToText = (markdown: string): string =>
	markdown
		.split(/\r?\n/)
		.filter(
			(line) =>
				!FENCE_LINE.test(line) &&
				!(RULE_LINE.test(line) && line.includes("---"))
		)
		.map((line) => line.replace(LINE_MARKER, ""))
		.join(" ")
		.replace(/\\([\\`*_{}[\]()#+\-.!|>~])/g, "$1")
		.replace(/!?\[([^\]]{0,500})\]\([^)]{0,2000}\)/g, "$1")
		.replace(/\[([^\]]{0,500})\]\[[^\]]{0,200}\]/g, "$1")
		.replace(/<(https?:[^>\s]{1,2000})>/g, "$1")
		.replace(/<\/?[a-z][^<>]{0,500}>/gi, " ")
		.replace(/[|*_~`]+/g, " ")
		.replace(/\s+/g, " ")
		.trim();

/** Where `term` starts a word in `folded`. */
const wordStarts = (folded: string, term: string): number[] => {
	const starts: number[] = [];
	for (
		let index = folded.indexOf(term);
		index !== -1;
		index = folded.indexOf(term, index + 1)
	) {
		if (index === 0 || !WORD_CHAR.test(folded[index - 1])) starts.push(index);
	}
	return starts;
};

const startsAWord = (folded: string, term: string) =>
	wordStarts(folded, term).length > 0;

type NewEntry = Omit<HelpSearchEntry, "order" | "foldedTitle" | "foldedBody">;

/**
 * A section and what's inside it. A tabbed article is found by its tabs, as
 * that's all the page shows of it; its single-article text is a draft.
 */
const sectionEntries = (
	topic: ReaderTopic,
	section: ReaderSection
): NewEntry[] => {
	const keys = { topicKey: topic.key, sectionKey: section.key };
	const where = `${topic.topicTitle} › ${section.title}`;
	const sectionTo = helpTopicPath(topic.key, section.key);
	const entry = (body: string): NewEntry => ({
		kind: "section",
		title: section.title,
		body,
		context: topic.topicTitle,
		to: sectionTo,
		...keys,
	});

	switch (section.type) {
		case "markdown": {
			const { tabMode, markdownContent, markdownTabs } = section.markdown;
			if (!tabMode) return [entry(markdownToText(markdownContent))];
			return [
				entry(""),
				...markdownTabs.map((tab, index): NewEntry => ({
					kind: "tab",
					title: tab.title,
					body: markdownToText(tab.content),
					context: where,
					to: helpTopicPath(topic.key, section.key, { tab: index + 1 }),
					...keys,
				})),
			];
		}
		case "faq":
			return [
				entry(""),
				...section.faq.map((item): NewEntry => ({
					kind: "faq",
					title: item.question,
					body: markdownToText(item.answer),
					context: where,
					to: helpTopicPath(topic.key, section.key, { faq: item.key }),
					...keys,
				})),
			];
		case "folder":
			return [
				entry(""),
				...section.folder.flatMap((folder) =>
					folder.videos.map((video): NewEntry => ({
						kind: "video",
						title: video.title,
						body: video.description ?? "",
						context: `${where} › ${folder.title}`,
						to: sectionTo,
						...keys,
					}))
				),
			];
	}
};

const topicEntries = (topic: ReaderTopic): NewEntry[] => [
	{
		kind: "topic",
		title: topic.topicTitle,
		body: topic.description,
		context: "Topic",
		to: helpTopicPath(topic.key),
		topicKey: topic.key,
	},
	...topic.children.flatMap((section) => sectionEntries(topic, section)),
];

const defaultFaqEntries = (defaultFaq: HelpDefaultFaq): NewEntry[] =>
	defaultFaq.faq.map((item) => ({
		kind: "defaultFaq",
		title: item.question,
		body: markdownToText(item.answer),
		context: defaultFaq.title || "Frequently asked questions",
		to: helpFaqPath(item.key),
	}));

/**
 * One entry per thing a reader can look for: each topic, section, markdown
 * tab, FAQ item and video, and each default FAQ. Build it from the reader
 * view, so hidden and module-hidden content can't be found.
 */
export const buildHelpSearchIndex = (
	topics: ReaderTopic[],
	defaultFaq?: HelpDefaultFaq | null
): HelpSearchEntry[] =>
	[
		...topics.flatMap(topicEntries),
		...(defaultFaq ? defaultFaqEntries(defaultFaq) : []),
	].map((entry, order) => ({
		...entry,
		order,
		foldedTitle: foldText(entry.title).folded,
		foldedBody: foldText(entry.body).folded,
	}));

const matches = (entry: HelpSearchEntry, terms: string[]) =>
	terms.every(
		(term) =>
			startsAWord(entry.foldedTitle, term) ||
			startsAWord(entry.foldedBody, term)
	);

/**
 * The entries every term matches, best first: more terms in the title, then
 * topics, then sections and tabs, then FAQs and videos, then index order.
 * No terms means no search, so nothing is returned.
 */
export const searchHelp = (
	index: HelpSearchEntry[],
	terms: string[]
): HelpSearchEntry[] => {
	if (!terms.length) return [];
	const titleHits = (entry: HelpSearchEntry) =>
		terms.filter((term) => startsAWord(entry.foldedTitle, term)).length;
	return index
		.filter((entry) => matches(entry, terms))
		.map((entry) => ({
			entry,
			score: titleHits(entry) * 10 + KIND_WEIGHT[entry.kind],
		}))
		.sort((a, b) => b.score - a.score || a.entry.order - b.entry.order)
		.map(({ entry }) => entry);
};

/** Keys of the topics with any match, in or under the topic. */
export const matchingTopicKeys = (
	index: HelpSearchEntry[],
	terms: string[]
): Set<string> =>
	new Set(
		index
			.filter((entry) => terms.length > 0 && matches(entry, terms))
			.map((entry) => entry.topicKey)
			.filter((key): key is string => !!key)
	);

/** Keys of the topic's sections with any match, in the section or its tabs, FAQs and videos. */
export const matchingSectionKeys = (
	index: HelpSearchEntry[],
	terms: string[],
	topicKey: string
): Set<string> =>
	new Set(
		index
			.filter(
				(entry) =>
					entry.topicKey === topicKey &&
					terms.length > 0 &&
					matches(entry, terms)
			)
			.map((entry) => entry.sectionKey)
			.filter((key): key is string => !!key)
	);

/** Sorted, merged ranges, so overlapping highlights don't nest. */
const mergeRanges = (ranges: TextRange[]): TextRange[] =>
	[...ranges]
		.sort((a, b) => a[0] - b[0])
		.reduce<TextRange[]>((merged, range) => {
			const last = merged[merged.length - 1];
			if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1]);
			else merged.push([range[0], range[1]]);
			return merged;
		}, []);

/** Where each term starts a word in `text`, as ranges of the original text. */
export const highlightRanges = (text: string, terms: string[]): TextRange[] => {
	const { folded, map } = foldText(text);
	const ranges: TextRange[] = [];
	for (const term of terms) {
		for (const start of wordStarts(folded, term)) {
			ranges.push([map[start], map[start + term.length - 1] + 1]);
		}
	}
	return mergeRanges(ranges);
};

/** About `SNIPPET_LENGTH` characters of `body` around its first match, cut at spaces. */
const makeSnippet = (body: string, terms: string[]): string => {
	if (!body) return "";
	const { folded, map } = foldText(body);
	const firstHit = Math.min(
		...terms.map((term) => wordStarts(folded, term)[0] ?? Infinity)
	);
	const leadStart =
		Number.isFinite(firstHit) && map[firstHit] > SNIPPET_LEAD
			? map[firstHit] - SNIPPET_LEAD
			: 0;
	// A match near the end still gets a full-length snippet.
	let start = Math.max(0, Math.min(leadStart, body.length - SNIPPET_LENGTH));
	let end = Math.min(body.length, start + SNIPPET_LENGTH);
	if (start > 0) {
		const space = body.indexOf(" ", start);
		start = space !== -1 && space < map[firstHit] ? space + 1 : start;
	}
	if (end < body.length) {
		const space = body.lastIndexOf(" ", end);
		end = space > start ? space : end;
	}
	return (
		(start > 0 ? ELLIPSIS : "") +
		body.slice(start, end).trim() +
		(end < body.length ? ELLIPSIS : "")
	);
};

/** What a result shows: its title and a snippet, with the matching words marked. */
export const describeResult = (
	entry: HelpSearchEntry,
	terms: string[]
): HelpSearchHighlights => {
	const snippet = makeSnippet(entry.body, terms);
	return {
		titleRanges: highlightRanges(entry.title, terms),
		snippet,
		snippetRanges: highlightRanges(snippet, terms),
	};
};
